import { FieldValues } from 'react-hook-form';
import axios from 'axios';

import { authenticationWorker } from './util';

const { VITE_WORKER_URL } = import.meta.env;

export const KOBE_PORTAL_FEATURE_ACCESS = {
  CONSUMABLES_HIDE_PRICE: 'cons_hide_price',
  CONSUMABLES_SHOW_PHOTO_VIEW: 'cons_show_photo_view',
  CHECKOUT_HIDE_PRICES_ON_CART: 'checkout_hide_price',
  CHECKOUT_OVER_100_HIDE: 'checkout_over_100_hide',
  SHOW_COURIER_TAB: 'courier_show_tab',
  SHOW_REFERRAL_TAB: 'referral_show_tab',
  SHOW_MARKETING_TAB: 'marketing_show_tab',
  SHOW_REFERRAL_SOON_BADGE: 'referral_show_soon',
  BLOCK_ADD_NEW_ADDRESS: 'address_add_block',
  HIDE_USER_DOCTOR_CODE: 'users_hide_doctor_code',
  HIDE_IT_INFORMATION_ON_SETTINGS: 'hide_it_information',
  HIDE_PRICE_ON_ORDERS: 'hide_price_order',
  HIDE_CONSUMABLES_TAB: 'consumables_hide_tab',
  ONLY_SHOW_COURIER_TAB: 'only_courier',
  CCM_BDM_HIDE_USERS: 'ccm_bdm_hide_profile_and_users',
  CCM_BDM_HIDE_BILLING_AND_ROLE: 'ccm_bdm_hide_profile_and_users',
  SHOW_QUOTES_TAB: 'show_quotes',
  HIDE_ONBOARDING: 'hide_onboarding',
  CCM_ORDER_VIEW: 'ccm_order_view',

  // ONLY FOR VETNOSTICS
  ADD_VETERINARIAN_AS_USER: 'add_vet_as_user',
  // ONLY FOR THE PATHOLOGOIES
  ADD_CLINICIAN_AS_USER: 'add_cli_as_user',
};

export const checkIfCompanyHasAccessToFeature = (ACCESS_KEY: string) => {
  const companyFeatureAccess = sessionStorage.getItem('company-feature-access');
  if (companyFeatureAccess) {
    try {
      const companyFeatureList = JSON.parse(companyFeatureAccess);
      if (Array.isArray(companyFeatureList)) {
        return companyFeatureList.includes(ACCESS_KEY);
      }
      if (typeof companyFeatureList === 'object' && companyFeatureList !== null) {
        return Boolean(companyFeatureList[ACCESS_KEY]);
      }
    } catch (err) {
      return companyFeatureAccess.includes(ACCESS_KEY);
    }
  }
  return false;
};

export type OnboardingSteps = Record<string, boolean>;

export const ONBOARDING_STEPS_SESSION_KEY = 'company-onboarding-steps';

export const getOnboardingStepsFromSession = (): OnboardingSteps => {
  const storedSteps = sessionStorage.getItem(ONBOARDING_STEPS_SESSION_KEY);
  if (!storedSteps) return {};

  try {
    const parsedSteps = JSON.parse(storedSteps);
    return parsedSteps && typeof parsedSteps === 'object' && !Array.isArray(parsedSteps)
      ? parsedSteps
      : {};
  } catch {
    return {};
  }
};

const saveOnboardingStepsToSession = (steps: OnboardingSteps) => {
  sessionStorage.setItem(ONBOARDING_STEPS_SESSION_KEY, JSON.stringify(steps));
};

export type UpdateOnboardingStepsPayload = {
  companyId: string | number;
  onboardingSteps: OnboardingSteps;
};

export const updateOnboardingSteps = async (
  payload: UpdateOnboardingStepsPayload,
): Promise<OnboardingSteps | false> => {
  const currentSteps = getOnboardingStepsFromSession();
  const changedSteps = Object.entries(payload.onboardingSteps).reduce<OnboardingSteps>(
    (acc, [stepKey, completed]) => {
      // Onboarding is monotonic. Only promote an incomplete step to true;
      // incomplete values are intentionally not sent to the worker.
      if (completed === true && currentSteps[stepKey] !== true) {
        return {
          ...acc,
          [stepKey]: true,
        };
      }
      return acc;
    },
    {},
  );

  if (Object.keys(changedSteps).length === 0) {
    return currentSteps;
  }

  const changedPayload = {
    ...payload,
    onboardingSteps: changedSteps,
  };

  try {
    const authKey = await authenticationWorker();
    const response = await axios.put(
      `${VITE_WORKER_URL}/esync/api/onboardingSteps/`,
      changedPayload,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    const onboardingSteps = response.data?.response?.onboardingSteps;
    if (onboardingSteps && typeof onboardingSteps === 'object') {
      saveOnboardingStepsToSession(onboardingSteps);
      return onboardingSteps;
    }
    return false;
  } catch (error) {
    console.error('Error updating onboarding steps:', error);
    return false;
  }
};

export const updateOnboardingStep = async (
  companyId: string | number,
  stepId: string,
  completed: boolean,
): Promise<OnboardingSteps | false> => {
  const onboardingKey = stepId.indexOf('onboarding_') === 0 ? stepId : `onboarding_${stepId}`;
  return updateOnboardingSteps({
    companyId,
    onboardingSteps: {
      [onboardingKey]: completed,
    },
  });
};

export type B2BAddressNode = {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  id: string;
};

export type B2BAddress = {
  node: B2BAddressNode;
};

export type AddressRequestPayload = {
  label: string;
  firstName: string;
  lastName: string;
  company: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  phoneNumber: string;
  address1: string;
  zip_code: string;
};

export type JobPayload = {
  companyId: string | number;
  address: Record<string, unknown> | B2BAddressNode | undefined;
  sample_reference: string;
  specimen_location_details: string;
  comments: string;
  priority: string;
  priority_reason: string;
  companyInfo: {
    companyName: string;
    id: string | number;
  };
  customerEmailAddress: string;
  courierKey?: string;
};

export type ReferralPadFormPayload = {
  clinicians: {
    value: string;
  }[];
  'company-id': string | number;
  'copy-to-address': string;
  'copy-to-name': string;
  'form-type': string;
  'pad-qty': number;
  'provider-number': string;
  'referral-tests': {
    value: string;
  }[];
  'surgery-address': string;
  'additional-comments': string;
};

export type RoleUpdatePayload = {
  companyId: string;
  userId: string;
  companyRoleId: string;
};

export type ProductCategoryVisibilityPayload = {
  companyId: string;
};

export type PaymentMethodPayload = {
  companyId: string;
};

export type CustomerGroupPayload = {
  companyId: string;
  pathology?: string | null;
  businessType?: string | null;
  customerEmail?: string | null;
  customerId?: number | null;
  customerFirstName?: string | null;
  customerLastName?: string | null;
  customerRole?: string | null;
  eReferralSetup?: boolean | null;
};

export type DefaultAddressPayload = {
  companyId: string;
  label?: string;
};

export type ClinicianPayloadEntry = {
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  companyRoleId?: number;
  type?: string;
  extraFields?: {
    fieldName: string;
    fieldValue: string;
  }[];
};

export type CreateCliniciansPayload = {
  companyId: string | number;
  type: string;
  clinicians: ClinicianPayloadEntry[];
};

export const getCourierByCode = async (courierCode: string, courierPass: string) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/courier-code/${courierCode}/${courierPass}`,
      {
        headers: {
          Authorization: authKey,
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getTests: (type: 'pathology' | 'vetinary' | 'specialist') => Promise<
  | {
      [key: string]: string;
    }
  | false
> = async (type: 'pathology' | 'vetinary' | 'specialist') => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/config/${type.toUpperCase()}_TESTS`,
      {
        headers: {
          Authorization: authKey,
        },
        withCredentials: false,
      },
    );
    return JSON.parse(response.data);
  } catch (error) {
    return false;
  }
};

export const getCompanyBySurgeryCode = async (surgeryCode: string) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(`${VITE_WORKER_URL}/esync/api/surgeryExists/${surgeryCode}`, {
      headers: {
        Authorization: authKey,
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getCompanyByAdminEmail = async (email: string) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/companyAdminEmailExists/${email}`,
      {
        headers: {
          Authorization: authKey,
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getSquareInvoice = async (
  orderId: string | number,
  paymentMethod: string | undefined,
  dateCreateAt: string | number | undefined,
) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.post(
      `${VITE_WORKER_URL}/esync/api/square/invoice`,
      {
        orderId,
        paymentMethod,
        dateCreateAt,
      },
      {
        headers: {
          Authorization: authKey,
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const createAddressRequest = async (
  payload: FieldValues,
  email: string,
  companyName: string,
  companyId: string,
) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.post(
      `${VITE_WORKER_URL}/esync/api/addressRequest/`,
      {
        companyId,
        company: {
          companyName,
          companyEmail: email,
        },
        address: payload,
      },
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getOrderPaymentStatus = async (payload: []) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.post(
      `${VITE_WORKER_URL}/esync/api/getOrderPaymentStatus/`,
      payload,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return response.data.response.orders;
  } catch (error) {
    return false;
  }
};

export const createCourierJob = async (payload: JobPayload) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.post(`${VITE_WORKER_URL}/esync/api/job/`, payload, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const createReferralPadRequest = async (payload: ReferralPadFormPayload) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.post(`${VITE_WORKER_URL}/esync/api/pads/`, payload, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getProductsByCompanyId = async (companyId: string, type: string) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(`${VITE_WORKER_URL}/esync/api/products/${companyId}/${type}`, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    console.log(`FAILED TO RETRIEVE ITEM${error}`);
    return {
      response: {
        products: [],
      },
    };
  }
};

export const getSubcategoriesVaccine = async () => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(`${VITE_WORKER_URL}/esync/api/vaccinesSubcategories`, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getReferralPadsForms = async (companyId: string) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/referralPads/${companyId}/forms`,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const updateProductCategoryVisibility = async (
  payload: ProductCategoryVisibilityPayload,
) => {
  try {
    console.log('---- PRODUCT CATEGORY VISIBILITY UPDATE ----');
    const authKey = await authenticationWorker();
    const response = await axios.put(`${VITE_WORKER_URL}/esync/api/productGroups/`, payload, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    return false;
  }
};

export const updateDefaultAddress = async (payload: DefaultAddressPayload) => {
  try {
    console.log('---- DEFAULT ADDRESS UPDATE ----');
    const authKey = await authenticationWorker();
    const response = await axios.put(`${VITE_WORKER_URL}/esync/api/setDefaultAddress/`, payload, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    return false;
  }
};

export const syncProductPricingAndPaymentMethods = async (payload: CustomerGroupPayload) => {
  try {
    console.log('---- syncProductPricingAndPaymentMethods UPDATE ----');
    const authKey = await authenticationWorker();
    const response = await axios.put(
      `${VITE_WORKER_URL}/esync/api/syncProductPricingAndPaymentMethods/`,
      payload,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    const { data } = response;
    if (data?.response) {
      if (data.response.pathology) {
        sessionStorage.setItem('company-pathology', data.response.pathology);
      }
      if (data.response.marketingTabAccess) {
        sessionStorage.setItem('company-marketing-tab-access', data.response.marketingTabAccess);
      }
      if (data.response.featureAccess) {
        sessionStorage.setItem(
          'company-feature-access',
          JSON.stringify(data.response.featureAccess),
        );
      }
      if (data.response.onboardingSteps) {
        saveOnboardingStepsToSession(data.response.onboardingSteps);
      }
      sessionStorage.setItem('kobe-sync-check', 'true');
      window.dispatchEvent(
        new CustomEvent('kobe-company-sync-completed', { detail: data.response }),
      );
    }
    return data;
  } catch (error) {
    return false;
  }
};

export const markCheckoutOrderType = async (type: 'consumables' | 'vaccines') => {
  try {
    const { data } = await axios.get(`/api/storefront/carts`);
    if (data[0]) {
      const authKey = await authenticationWorker();
      await axios.post(
        `${VITE_WORKER_URL}/esync/api/tagCart`,
        {
          cartId: data[0].id,
          tag: type,
        },
        {
          headers: {
            Authorization: authKey,
            'Content-Type': 'application/json',
          },
          withCredentials: false,
        },
      );
      return data;
    }
    return data;
  } catch (error) {
    return false;
  }
};

export const sendCustomCartData = async (key: string, value: string, overrideCartId?: string) => {
  try {
    let cartId = overrideCartId;

    if (!cartId) {
      const { data } = await axios.get(`/api/storefront/carts`);
      if (data[0]) {
        cartId = data[0].id;
      }
    }

    if (cartId) {
      const authKey = await authenticationWorker();
      await axios.post(
        `${VITE_WORKER_URL}/esync/api/cartCustomData`,
        {
          cartId,
          key,
          value,
        },
        {
          headers: {
            Authorization: authKey,
            'Content-Type': 'application/json',
          },
          withCredentials: false,
        },
      );
      return true;
    }
    return false;
  } catch (error) {
    return false;
  }
};

export const checkIfCourierChannelExists = async (companyId: string) => {
  const allowCourierCompanyId = sessionStorage.getItem(`allow-courier-${companyId}`);
  if (
    sessionStorage.getItem(`loading-allow-courier-${companyId}`) === 'true' ||
    allowCourierCompanyId
  ) {
    return allowCourierCompanyId === 'true';
  }
  try {
    const authKey = await authenticationWorker();
    sessionStorage.setItem(`loading-allow-courier-${companyId}`, 'true');
    const { data } = await axios.get(`${VITE_WORKER_URL}/esync/api/nowGoChannel/${companyId}`, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    sessionStorage.setItem(`allow-courier-${companyId}`, data && data.exists);
    return data && data.exists;
  } catch (error) {
    return false;
  }
};

export const checkIfSalesRep = async (email: string) => {
  const isSalesRep = sessionStorage.getItem(`is-sales-rep-${email}`);
  if (sessionStorage.getItem(`loading-is-sales-rep-${email}`) === 'true' || isSalesRep) {
    return isSalesRep === 'true';
  }
  try {
    const authKey = await authenticationWorker();
    sessionStorage.setItem(`loading-is-sales-rep-${email}`, 'true');
    const { data } = await axios.get(`${VITE_WORKER_URL}/esync/api/salesRepCheck/${email}`, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    sessionStorage.setItem(`is-sales-rep-${email}`, data && data.is_sales_rep);
    return data && data.is_sales_rep;
  } catch (error) {
    return false;
  }
};

export const getJobsByCompanyId = async (
  companyId: number | string,
  customerEmailAddress: string,
) => {
  console.log(`GET JOBS BY COMPANY ID --- ${companyId}`);
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/nowGoJobs/${companyId}/${customerEmailAddress}`,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    console.log(response.data);
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getAllJobsByCompanyId = async (
  companyId: number | string,
  customerEmailAddress: string,
) => {
  console.log(`GET ALL JOBS BY COMPANY ID --- ${companyId}`);
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/nowGoJobsAll/${companyId}/${customerEmailAddress}`,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    console.log(response.data);
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getLatestJobByCourierCode = async (
  courierCode: string,
  courierPass: string,
  extRef?: string,
) => {
  console.log(`GET LATEST JOB BY COURIER CODE --- ${courierCode}`);
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/nowGoJobLatestByCourierCode/${courierCode}?cour_pass=${courierPass}&ext_ref=${extRef}`,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    console.log(response.data);
    return response.data;
  } catch (error) {
    return false;
  }
};

export const fetchAllCompanyAddresses = async (bcCustomerId: number | string) => {
  console.log(`FETCH ALL COMPANY ADDRESSES --- ${bcCustomerId}`);
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/checkout/addresses?bcCustomerId=${bcCustomerId}`,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    console.log(response.data);
    return response.data;
  } catch (error) {
    return false;
  }
};

export const updateCompanyBillingEmail = async (
  companyId: number | string,
  billingEmail: string,
) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.put(
      `${VITE_WORKER_URL}/esync/api/updateCompanyBillingEmail`,
      {
        companyId,
        billingEmail,
      },
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const updateCompanyExtraFields = async (
  companyId: number | string,
  extraFields: Array<{ fieldName: string; fieldValue: string }>,
) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.put(
      `${VITE_WORKER_URL}/esync/api/updateCompanyExtraFields`,
      {
        companyId,
        extraFields,
      },
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const getCompanyBillingEmail = async (companyId: number | string) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.get(
      `${VITE_WORKER_URL}/esync/api/getCompanyBillingEmail/${companyId}`,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return response.data.email;
  } catch (error) {
    return '';
  }
};

export const cancelCourierJob = async (jobReferenceId: string, customerEmailAddress: string) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.post(
      `${VITE_WORKER_URL}/esync/api/job/${jobReferenceId}/cancel`,
      {
        customerEmailAddress,
      },
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return response.data;
  } catch (error) {
    return false;
  }
};

export const createClinicians = async (payload: CreateCliniciansPayload) => {
  try {
    const authKey = await authenticationWorker();
    const response = await axios.post(`${VITE_WORKER_URL}/esync/api/createClinicians`, payload, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return response.data;
  } catch (error) {
    console.error('Failed to create clinicians:', error);
    return false;
  }
};

export interface CollectionCenterOrdersResponse {
  success: boolean;
  is_sales_rep?: boolean;
  message?: string;
  orders?: Array<{
    node: {
      orderId: string;
      companyName: string;
      totalIncTax: string;
      firstName: string;
      lastName: string;
      paymentStatus: string;
      status: string;
      createdAt: string;
    };
  }>;
}

export const getCollectionCenterOrders = async (
  email: string,
): Promise<CollectionCenterOrdersResponse> => {
  try {
    const authKey = await authenticationWorker();
    const { data } = await axios.get(
      `${VITE_WORKER_URL}/esync/api/collectionCenterOrders/${email}`,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return data;
  } catch (error) {
    console.error('Failed to get collection center orders', error);
    return {
      success: false,
      is_sales_rep: false,
      orders: [],
    };
  }
};

export interface CollectionCenterOrderDetailPayload {
  orderId: string | number;
  email: string;
}

export interface CollectionCenterOrderDetailItem {
  id?: string | number;
  productId?: string | number;
  name?: string;
  sku?: string;
  quantity?: number;
  quantityShipped?: number;
  unitPrice?: string;
  totalPrice?: string;
  imageUrl?: string;
  options?: Array<{
    name?: string;
    value?: string;
  }>;
}

export interface CollectionCenterOrderDetailShipment {
  id?: string | number;
  trackingNumber?: string;
  trackingCarrier?: string;
  trackingLink?: string;
  shippedDate?: string;
  comments?: string;
}

export interface CollectionCenterOrderData {
  orderId: string;
  companyId?: string | number;
  company_id?: string | number;
  poNumber?: string;
  companyName?: string;
  status?: string;
  paymentStatus?: string;
  paymentMethod?: string;
  dateCreated?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  summary?: {
    subtotal?: string;
    shipping?: string;
    tax?: string;
    discount?: string;
    total?: string;
    currencySymbol?: string;
  };
  shippingAddress?: {
    firstName?: string;
    lastName?: string;
    company?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone?: string;
    shippingMethod?: string;
  };
  billingAddress?: {
    firstName?: string;
    lastName?: string;
    company?: string;
    address1?: string;
    address2?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    phone?: string;
  };
  products?: CollectionCenterOrderDetailItem[];
  shipments?: CollectionCenterOrderDetailShipment[];
  comments?: string;
  customerMessage?: string;
  orderComments?: string;
}

export interface CollectionCenterOrderDetailResponse {
  success: boolean;
  is_sales_rep?: boolean;
  message?: string;
  order?: CollectionCenterOrderData | null;
}

export const getCollectionCenterOrderDetails = async (
  payload: CollectionCenterOrderDetailPayload,
): Promise<CollectionCenterOrderDetailResponse> => {
  try {
    const authKey = await authenticationWorker();
    const { data } = await axios.post(
      `${VITE_WORKER_URL}/esync/api/collectionCenterOrderDetails`,
      payload,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return data;
  } catch (error) {
    console.error('Failed to get collection center order details', error);
    return {
      success: false,
      message: 'Failed to fetch order details',
      order: null,
    };
  }
};

export interface ApproveCollectionCenterOrderItem {
  id?: string | number;
  productId?: string | number;
  sku?: string;
  name?: string;
  quantity: number;
  unitPrice?: string;
  totalPrice?: string;
}

export interface ApproveCollectionCenterItemChange {
  id?: string | number;
  sku?: string;
  name?: string;
  action: 'added' | 'updated' | 'removed' | 'unchanged';
  originalQuantity: number;
  newQuantity: number;
}

export interface ApproveCollectionCenterOrderPayload {
  orderId: string | number;
  email: string;
  approve: boolean;
  items?: ApproveCollectionCenterOrderItem[];
  itemsChanged?: ApproveCollectionCenterItemChange[];
}

export interface ApproveCollectionCenterOrderResponse {
  success: boolean;
  message?: string;
}

export const approveCollectionCenterOrder = async (
  payload: ApproveCollectionCenterOrderPayload,
): Promise<ApproveCollectionCenterOrderResponse> => {
  try {
    const authKey = await authenticationWorker();
    const { data } = await axios.post(
      `${VITE_WORKER_URL}/esync/api/collectionCenterOrderApprove`,
      payload,
      {
        headers: {
          Authorization: authKey,
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      },
    );
    return data;
  } catch (error) {
    console.error('Failed to approve collection center order', error);
    return {
      success: false,
      message: 'Failed to approve order',
    };
  }
};

export interface CompanyProductListPayload {
  companyId: string | number;
  type: string;
  email: string;
}

export interface CompanyProductItem {
  id: number | string;
  productId?: number | string;
  variantId?: number | string;
  baseSku?: string;
  sku?: string;
  variantSku?: string;
  productName?: string;
  name?: string;
  basePrice?: string | number;
  price?: string | number;
  unitName?: string;
  subcategories?: string[];
  primaryImage?: string;
  productImageThumbnail?: string | null;
  [key: string]: unknown;
}

export interface CompanyProductListResponse {
  success?: boolean;
  response?: {
    products: Array<{ node: CompanyProductItem }>;
  };
  products?: Array<{ node: CompanyProductItem } | CompanyProductItem>;
}

export const getCompanyProductList = async (
  payload: CompanyProductListPayload,
): Promise<CompanyProductListResponse> => {
  try {
    const authKey = await authenticationWorker();
    const { data } = await axios.post(`${VITE_WORKER_URL}/esync/api/companyProductList`, payload, {
      headers: {
        Authorization: authKey,
        'Content-Type': 'application/json',
      },
      withCredentials: false,
    });
    return data;
  } catch {
    return {
      response: {
        products: [],
      },
    };
  }
};
